import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, X, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { colorForTeam } from './CompareTeamCalendar';

const HEADER_H = 56;
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ordinal = (d) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = d % 100;
  return `${d}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const dateLabel = (iso) => {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1]} ${ordinal(d)}`;
};

const countsText = (e) => {
  const parts = [];
  const doors = e.doors || 0;
  const windows = e.windows || 0;
  if (doors > 0) parts.push(`${doors} Door${doors > 1 ? 's' : ''}`);
  if (windows > 0) parts.push(`${windows} Window${windows > 1 ? 's' : ''}`);
  if (parts.length === 0) return (e.job_type || '').toUpperCase() === 'SERVICE' ? 'Service' : '1 Job';
  return parts.join(' and ');
};

const multiDayNote = (e) => {
  const dur = parseInt(e.duration_days) || 1;
  if (dur <= 1) return '';
  let d1 = e.date;
  let d2 = null;
  if (e.dates && e.dates.length > 1) {
    const sorted = [...e.dates].sort();
    d1 = sorted[0];
    d2 = sorted[sorted.length - 1];
  } else {
    const [y, m, d] = e.date.split('-').map(Number);
    const end = new Date(y, m - 1, d + dur - 1);
    d2 = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  }
  const [, m1, day1] = d1.split('-').map(Number);
  const [, m2, day2] = d2.split('-').map(Number);
  const sameMonth = m1 === m2;
  const left = sameMonth ? ordinal(day1) : `${ordinal(day1)} ${MONTHS_SHORT[m1 - 1]}`;
  const right = `${ordinal(day2)} ${MONTHS_SHORT[m2 - 1]}`;
  const joiner = dur === 2 ? 'and' : 'to';
  return ` (${dur} days - ${left} ${joiner} ${right})`;
};

const WEEKDAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const weekdayOf = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return WEEKDAYS_FULL[new Date(y, m - 1, d).getDay()];
};

const targetDateLabel = (fromIso, toIso) => {
  const [, fm] = fromIso.split('-').map(Number);
  const [, tm, td] = toIso.split('-').map(Number);
  return tm === fm ? ordinal(td) : `${MONTHS_SHORT[tm - 1]} ${ordinal(td)}`;
};

const stopPhrase = (e) => {
  if (!e.stop_choice) return '';
  if (e.stop_choice === '1st') {
    return e.current_stop === '1st'
      ? '. Add this on 1st stop, and move current job for the 2nd stop, please'
      : '. Add it on the 1st stop, please';
  }
  return e.current_stop === '2nd'
    ? '. Add this on 2nd stop, and move current job for the 1st stop, please'
    : '. Add it on the 2nd stop, please';
};

export const entryLine = (e) => {
  if (e.type === 'block') return `Block ${e.team}`;
  if (e.type === 'note') return `${e.team}: ${e.text}`;
  const dest = e.to_team === 'ANY' ? 'any other crew' : e.to_team;
  if (e.to_date && e.to_date !== e.date) {
    const [, , fd] = e.date.split('-').map(Number);
    let line = `From ${e.from_team}: Try to move ${weekdayOf(e.date)}'s job (${ordinal(fd)}) to ${weekdayOf(e.to_date)} (${targetDateLabel(e.date, e.to_date)}): ${countsText(e)} (${e.customer_last} - ${e.city})`;
    if (e.to_team !== e.from_team) line += ` -> Move to ${dest}`;
    return line + multiDayNote({ ...e, date: e.to_date, dates: null }) + stopPhrase(e);
  }
  return `From ${e.from_team}: ${countsText(e)} (${e.customer_last} - ${e.city}) -> Move to ${dest}${multiDayNote(e)}${stopPhrase(e)}`;
};

const TYPE_ORDER = { move: 0, note: 1, block: 2 };
const WEEK_SEPARATOR = '________________________';
export const DEFAULT_GREETING = 'Hey bud..\nhere are the jobs we will need to rearrange, please:';
export const DEFAULT_SIGNATURE = 'Thank you, Richard..\nPlease, let me know if you have any questions or concerns.\n\nJohnny Garbim';

const mondayWeekKey = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = (dt.getDay() + 6) % 7; // 0 = segunda
  dt.setDate(dt.getDate() - dow);
  return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
};

export const buildEmailText = (entries) => {
  const byDate = {};
  entries.forEach((e) => {
    if (!byDate[e.date]) byDate[e.date] = [];
    byDate[e.date].push(e);
  });
  const dates = Object.keys(byDate).sort();
  const blocksOut = [];
  let prevWeek = null;
  dates.forEach((d) => {
    const wk = mondayWeekKey(d);
    if (prevWeek && wk !== prevWeek) blocksOut.push(WEEK_SEPARATOR);
    prevWeek = wk;
    const list = byDate[d].sort((a, b) => {
      const t = (TYPE_ORDER[a.type] ?? 0) - (TYPE_ORDER[b.type] ?? 0);
      if (t !== 0) return t;
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
    blocksOut.push(`${dateLabel(d)}\n${list.map(entryLine).join('\n')}`);
  });
  return blocksOut.join('\n\n');
};

const ColoredLine = ({ line }) => (
  <>
    {line.split(/((?:->\s*)?Move to PM\d+|PM\d+)/g).map((p, k) => {
      const moveMatch = /^(?:->\s*)?Move to (PM\d+)$/.exec(p);
      if (moveMatch) return <span key={k} style={{ color: colorForTeam(moveMatch[1]), fontWeight: 700 }}>{p}</span>;
      if (/^PM\d+$/.test(p)) return <span key={k} style={{ color: colorForTeam(p), fontWeight: 700 }}>{p}</span>;
      return <span key={k}>{p}</span>;
    })}
  </>
);

const CompareChangesSheet = ({ open, onClose, entries, onDelete, onClearAll, pt, expandSignal }) => {
  const [minimized, setMinimized] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const [greeting, setGreeting] = useState(() => localStorage.getItem('compareEmailGreeting') ?? DEFAULT_GREETING);
  const [signature, setSignature] = useState(() => localStorage.getItem('compareEmailSignature') ?? DEFAULT_SIGNATURE);
  const [editTexts, setEditTexts] = useState(false);

  useEffect(() => {
    if (open) { setMinimized(false); setDragY(0); }
  }, [open]);

  useEffect(() => {
    if (open && expandSignal) setMinimized(false);
  }, [expandSignal, open]);

  useEffect(() => {
    if (open && !minimized) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open, minimized]);

  const body = useMemo(() => buildEmailText(entries), [entries]);
  const text = useMemo(() => [greeting.trim(), body, signature.trim()].filter(Boolean).join('\n\n'), [greeting, body, signature]);

  if (!open) return null;

  const onPointerDown = (e) => {
    startRef.current = { y: e.clientY, wasMin: minimized };
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };
  const onPointerMove = (e) => {
    if (!startRef.current) return;
    let dy = e.clientY - startRef.current.y;
    if (startRef.current.wasMin) dy = Math.min(0, dy);
    else dy = Math.max(0, dy);
    setDragY(dy);
  };
  const onPointerUp = () => {
    if (!startRef.current) { setDragging(false); return; }
    const { wasMin } = startRef.current;
    const dy = dragY;
    startRef.current = null;
    setDragging(false);
    setDragY(0);
    if (!wasMin && dy > 100) setMinimized(true);
    else if (wasMin && dy < -60) setMinimized(false);
    else if (wasMin && Math.abs(dy) < 8) setMinimized(false);
  };

  const buildHtml = () => {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const lineHtml = (ln) => ln.split(/((?:->\s*)?Move to PM\d+|PM\d+)/g).map((p) => {
      const mv = /^(?:->\s*)?Move to (PM\d+)$/.exec(p);
      if (mv) return `<span style="color:${colorForTeam(mv[1])};font-weight:bold">${esc(p)}</span>`;
      if (/^PM\d+$/.test(p)) return `<span style="color:${colorForTeam(p)};font-weight:bold">${esc(p)}</span>`;
      return esc(p);
    }).join('');
    const body2 = text.split('\n').map((ln) => {
      if (ln.trim() === '') return '<div><br></div>';
      const isDate = /^[A-Z][a-z]{2} \d+(st|nd|rd|th)\s*$/.test(ln);
      const inner = lineHtml(ln);
      return `<div>${isDate ? `<b>${inner}</b>` : inner}</div>`;
    }).join('');
    return `<div style="font-family:Aptos, 'Segoe UI', Arial, sans-serif;font-size:12pt;color:#1e293b">${body2}</div>`;
  };

  const copyText = async () => {
    const html = buildHtml();
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
        toast.success(pt ? 'Copiado com cores e negrito!' : 'Copied with colors & bold!');
        return;
      }
      throw new Error('no ClipboardItem');
    } catch (err) {
      try {
        const div = document.createElement('div');
        div.contentEditable = 'true';
        div.innerHTML = html;
        div.style.position = 'fixed';
        div.style.left = '-9999px';
        document.body.appendChild(div);
        const range = document.createRange();
        range.selectNodeContents(div);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
        document.body.removeChild(div);
        toast.success(pt ? 'Copiado com cores e negrito!' : 'Copied with colors & bold!');
      } catch (err2) {
        await navigator.clipboard.writeText(text);
        toast.success(pt ? 'Texto copiado!' : 'Text copied!');
      }
    }
  };

  const baseTransform = minimized
    ? `translateY(calc(100% - ${HEADER_H}px - env(safe-area-inset-bottom, 0px)))`
    : 'translateY(0px)';
  const transform = dragging
    ? (minimized
        ? `translateY(calc(100% - ${HEADER_H}px - env(safe-area-inset-bottom, 0px) + ${dragY}px))`
        : `translateY(${dragY}px)`)
    : baseTransform;

  return (
    <div className={`fixed inset-0 z-[99990] ${minimized ? 'pointer-events-none' : ''}`} data-testid="compare-changes-sheet">
      {!minimized && (
        <div className="absolute inset-0 bg-black/40 transition-opacity" onClick={() => setMinimized(true)} data-testid="changes-sheet-backdrop" />
      )}
      <div
        className="absolute inset-x-0 bottom-0 flex flex-col bg-white rounded-t-2xl shadow-2xl pointer-events-auto"
        style={{
          height: 'calc(100dvh - 60px)',
          transform,
          transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          willChange: 'transform',
        }}
      >
        <div
          className="flex items-center gap-2 px-3 shrink-0 select-none cursor-grab active:cursor-grabbing border-b bg-slate-50 rounded-t-2xl touch-none relative"
          style={{ height: HEADER_H }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="changes-sheet-header"
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-10 h-1 rounded-full bg-slate-300" />
          <span className="text-lg">📧</span>
          <span className="font-semibold text-sm text-slate-800 flex-1 truncate">
            {pt ? 'Mudanças de Agenda' : 'Schedule Changes'} ({entries.length})
          </span>
          {minimized && (
            <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 mr-1">
              {pt ? 'aberto' : 'open'}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyText(); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            title={pt ? 'Copiar texto' : 'Copy text'}
            data-testid="changes-sheet-copy-btn"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            title={minimized ? 'Expandir' : 'Minimizar'}
            data-testid="changes-sheet-minimize-btn"
          >
            {minimized ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50"
            title={pt ? 'Limpar e fechar' : 'Clear & close'}
            data-testid="changes-sheet-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 bg-slate-50">
          {entries.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10" data-testid="changes-sheet-empty">
              {pt ? 'Nenhuma mudança registrada. Arraste um trabalho de uma equipe para outra no calendário.' : 'No changes yet. Drag a job from one team to another on the calendar.'}
            </p>
          ) : (
            <>
              <div className="bg-white border rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold text-slate-600 uppercase">{pt ? 'Texto do e-mail (copiar e colar)' : 'Email text (copy & paste)'}</h4>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setEditTexts(!editTexts)}
                      className={`flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 ${editTexts ? 'bg-amber-100 text-amber-700' : 'text-slate-500 bg-slate-100 hover:bg-slate-200'}`}
                      data-testid="changes-edit-texts-btn"
                    >
                      ✏️ {pt ? 'Editar textos' : 'Edit texts'}
                    </button>
                    <button
                      type="button"
                      onClick={copyText}
                      className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg px-2.5 py-1.5"
                      data-testid="changes-copy-inline-btn"
                    >
                      <Copy className="h-3.5 w-3.5" /> {pt ? 'Copiar' : 'Copy'}
                    </button>
                  </div>
                </div>
                {editTexts && (
                  <div className="space-y-2 mb-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    <div>
                      <label className="text-[10px] font-bold text-amber-700 uppercase">{pt ? 'Saudação (início)' : 'Greeting (top)'}</label>
                      <textarea
                        value={greeting}
                        onChange={(e) => { setGreeting(e.target.value); localStorage.setItem('compareEmailGreeting', e.target.value); }}
                        rows={2}
                        className="w-full border rounded-lg p-2 text-xs font-mono mt-0.5"
                        data-testid="changes-greeting-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-amber-700 uppercase">{pt ? 'Assinatura (final)' : 'Signature (bottom)'}</label>
                      <textarea
                        value={signature}
                        onChange={(e) => { setSignature(e.target.value); localStorage.setItem('compareEmailSignature', e.target.value); }}
                        rows={4}
                        className="w-full border rounded-lg p-2 text-xs font-mono mt-0.5"
                        data-testid="changes-signature-input"
                      />
                    </div>
                  </div>
                )}
                <div className="font-mono text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap select-all" data-testid="changes-email-text">
                  {text.split('\n').map((ln, i) => {
                    if (ln.trim() === '') return <div key={i}>&nbsp;</div>;
                    const isDate = /^[A-Z][a-z]{2} \d+(st|nd|rd|th)\s*$/.test(ln);
                    return (
                      <div key={i} className={isDate ? 'font-bold' : ''}>
                        <ColoredLine line={ln} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold text-slate-600 uppercase">{pt ? 'Registros' : 'Entries'} ({entries.length})</h4>
                  <button
                    type="button"
                    onClick={() => { if (window.confirm(pt ? 'Limpar TODOS os registros? Os trabalhos voltam ao normal no calendário.' : 'Clear ALL entries?')) onClearAll(); }}
                    className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg px-2.5 py-1.5"
                    data-testid="changes-clear-all-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {pt ? 'Limpar tudo' : 'Clear all'}
                  </button>
                </div>
                <div className="divide-y">
                  {[...entries].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((e) => (
                    <div key={e.id} className="flex items-center gap-2 py-2" data-testid={`changes-entry-${e.id}`}>
                      <span className="text-[11px] font-bold text-slate-600 w-16 shrink-0">{dateLabel(e.date)}</span>
                      <span className="text-xs text-slate-700 flex-1 min-w-0 truncate"><ColoredLine line={entryLine(e)} /></span>
                      <button
                        type="button"
                        onClick={() => onDelete(e)}
                        className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                        title={pt ? 'Desfazer' : 'Undo'}
                        data-testid={`changes-entry-delete-${e.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareChangesSheet;
